<script lang="ts">
  import { tStore } from '../../../i18n'

  // A single-tender delete carries the title to show; a bulk delete (the
  // Archive view's "Delete selected") carries only a count instead — the
  // union keeps this one modal reusable for both without a separate
  // always-present "open" prop (visibility is still driven by target !== null,
  // so the Dashboard's existing single-delete call site needs no changes).
  type DeleteTarget = { id: number; title: string } | { count: number }

  let {
    target,
    inFlight,
    onConfirm,
    onCancel,
  }: {
    target: DeleteTarget | null
    inFlight: boolean
    onConfirm: () => void
    onCancel: () => void
  } = $props()

  function isSingle(t: DeleteTarget): t is { id: number; title: string } {
    return 'title' in t
  }
</script>

{#if target}
  <!-- svelte-ignore a11y_click_events_have_key_events a11y_no_static_element_interactions -->
  <div class="dialog-backdrop" onclick={onCancel}>
    <div
      class="dialog"
      role="dialog"
      aria-modal="true"
      aria-labelledby="delete-dialog-title"
      tabindex="-1"
      onclick={(e) => e.stopPropagation()}
    >
      <h3 id="delete-dialog-title">
        {isSingle(target)
          ? $tStore('views.dashboard.deleteConfirmTitle')
          : $tStore('views.dashboard.deleteConfirmBulkTitle')}
      </h3>
      {#if isSingle(target)}
        <p class="dialog__body">{target.title}</p>
      {:else}
        <p class="dialog__body">
          {$tStore('views.dashboard.deleteConfirmBulkBody', { count: target.count })}
        </p>
      {/if}
      <p class="dialog__hint">{$tStore('views.dashboard.deleteConfirmBody')}</p>
      <div class="dialog__actions">
        <button type="button" class="btn btn--secondary" onclick={onCancel} disabled={inFlight}>
          {$tStore('common.cancel')}
        </button>
        <button type="button" class="btn btn--danger" onclick={onConfirm} disabled={inFlight}>
          {inFlight ? $tStore('common.loading') : $tStore('views.dashboard.deleteConfirm')}
        </button>
      </div>
    </div>
  </div>
{/if}

<style lang="scss">
  @use '../../../assets/styles/variables' as v;

  .dialog-backdrop {
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.45);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 100;
  }

  .dialog {
    background: v.$color-surface-elevated;
    border: 1px solid v.$color-border;
    border-radius: v.$radius-lg;
    box-shadow: v.$shadow-lg;
    padding: v.$space-lg;
    max-width: 28rem;
    width: calc(100% - 2rem);

    h3 {
      margin: 0 0 v.$space-sm;
      font-size: 1.125rem;
    }
  }

  .dialog__body {
    font-weight: 600;
    margin: 0 0 v.$space-xs;
    word-break: break-word;
  }

  .dialog__hint {
    color: v.$color-text-muted;
    font-size: 0.875rem;
    margin: 0 0 v.$space-lg;
  }

  .dialog__actions {
    display: flex;
    justify-content: flex-end;
    gap: v.$space-sm;
  }
</style>
